import type { NavTreeNode } from "@/utils/types";
import { getComponentFileFromNavTreeNodes } from "@/utils/jsx/extractElementsUtils";
import { compressNavTree } from "@/utils/navTreeCompression";
import {
  captureElementScreenshot,
  findElementByDomUniquePath,
  getElementDimensions,
  hasCapturableDom,
  hasCapturableDomByPath,
} from "@/components/Canvas/utils/utils";
import type { DragData } from "./instancesStore";
import { scheduleCanvasIdleTask } from "@/components/SimpleCanvas/simpleCanvasStore";

/**
 * Result of capturing content window instance data.
 * Includes both the blob (for GCS upload) and the data URL (for immediate display).
 */
export interface CaptureResult {
  blob: Blob;
  dataUrl: string;
  width?: number;
  height?: number;
  captureHotReloadTs?: number;
  dragData?: DragData;
}

/**
 * Result type that distinguishes between "not ready" and "failed" states.
 * - success: capture succeeded with data
 * - not_ready: element not available yet (should retry later)
 * - failed: actual capture error occurred (should count toward failure limit)
 */
export type CaptureOutcome =
  | { status: "success"; data: CaptureResult }
  | { status: "not_ready" }
  | { status: "failed" };

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Converts a Blob to a data URL string that can be used in <img src>.
 */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// CAPTURE FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Captures all data for an instance that requires contentWindow access:
 * - Screenshot blob (for GCS upload) and data URL (for immediate display)
 * - Element dimensions
 * - Drag data (file contents for drag & drop with children)
 *
 * Returns a CaptureOutcome discriminated union:
 * - "success": capture succeeded with all data
 * - "not_ready": element not available yet (will retry later)
 * - "failed": capture error (counts toward failure limit)
 */
export async function captureContentWindowInstanceData(
  domUniquePath: string,
  hotReloadTs: number | undefined,
  contentWindow: WindowProxy,
  canvasId: string,
  navNode: NavTreeNode | undefined,
): Promise<CaptureOutcome> {
  // Check if DOM element is ready for capture
  if (!hasCapturableDomByPath(contentWindow, domUniquePath)) {
    return { status: "not_ready" };
  }

  try {
    // Capture screenshot and dimensions in one idle task to avoid redundant DOM lookups
    // (findElementByDomUniquePath is O(n) and getBoundingClientRect triggers layout)
    const captureResult = await scheduleCanvasIdleTask(async () => {
      const el = findElementByDomUniquePath(
        contentWindow.document,
        domUniquePath,
      );
      if (!hasCapturableDom(el)) return null;

      const [blob, dimensions] = await Promise.all([
        captureElementScreenshot(el),
        Promise.resolve(getElementDimensions(el)),
      ]);

      return blob ? { blob, dimensions } : null;
    });

    if (!captureResult?.blob) {
      return { status: "not_ready" };
    }

    const { blob, dimensions } = captureResult;

    // Convert to data URL for immediate display
    const dataUrl = await scheduleCanvasIdleTask(() => blobToDataUrl(blob));

    // Extract drag data (file contents for drag & drop)
    let dragData: DragData | undefined;
    if (navNode) {
      const [result, compressedNavTree] = await Promise.all([
        scheduleCanvasIdleTask(() =>
          getComponentFileFromNavTreeNodes(canvasId, [navNode]),
        ),
        scheduleCanvasIdleTask(() => compressNavTree(navNode)),
      ]);

      if (result) {
        dragData = {
          fileContents: result.newFileContents,
          fileContentsSourceFilename: result.dummyFilePath ?? undefined,
          compressedNavTree: compressedNavTree ?? undefined,
        };
      }
    }

    return {
      status: "success",
      data: {
        blob,
        dataUrl,
        width: dimensions?.width,
        height: dimensions?.height,
        captureHotReloadTs: hotReloadTs,
        dragData,
      },
    };
  } catch (error) {
    console.error("[captureContentWindowInstanceData] Error:", error);
    return { status: "failed" };
  }
}

/**
 * Computes drag data for an instance (fileContents for drag & drop with children).
 */
export async function captureDragDataForInstance(
  canvasId: string,
  navNode: NavTreeNode,
): Promise<DragData> {
  try {
    const result = getComponentFileFromNavTreeNodes(canvasId, [navNode]);
    if (!result) return {};

    return {
      fileContents: result.newFileContents,
      fileContentsSourceFilename: result.dummyFilePath ?? undefined,
      compressedNavTree: compressNavTree(navNode) ?? undefined,
    };
  } catch (error) {
    console.error("[captureDragDataForInstance] Error:", error);
    return {};
  }
}
